import {
  useCallback,
  useEffect,
  useState,
  type FunctionComponent,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "../../../../components/ui/button";
// import DocumentSmall from "../../../../assets/svgs/campaigns/doc.svg";
import SearchIcon from "../../../../assets/svgs/common/search.svg";
import { Input } from "../../../../components/ui/input";
import { XIcon, Youtube } from "lucide-react";
import useUserStore from "../../../../store/socials.store";
import { debounce, handleHttpError } from "../../../../utils/utils";
import { useToast } from "../../../../hooks/use-toast";
import type { IPagination } from "../../../../types/models/pagination.models.types";
import { useSearchSocialsAdminHttp } from "../../../../hooks/http/admin/socials/search-socials.admin.http";
import {
  type ColumnDef,
  type ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import { InstagramIcon } from "../../../../assets/svgs/common/instagram.svg";
import millify from "millify";
import GrowthIcon from "../../../../assets/svgs/common/growth.svg";
import { DataTableComponents } from "../../../../components/tables/table.components";
import Spinner from "../../../../components/spinner.components";
import { Checkbox } from "../../../../components/ui/checkbox";
import { ScrollArea } from "../../../../components/ui/scroll-area";

import useExistingFixedPayoutStore from "../../../../store/existing-campaign-fixed-payout.store";
import useRemoveFixedPayoutStore from "../../../../store/remove-campaign-fixed-payout.store";
import {
  type IAddedFixedPayout,
  useGetFixedPayoutsAdminHttp,
} from "../../../../hooks/http/admin/campaigns/get-fixed-payouts.http";
import useSelectedFixedPayoutStore from "../../../../store/selected-campaign-fixed-payout.store";
import { useAddFixedPayoutToCampaignAdminHttp } from "../../../../hooks/http/admin/campaigns/add-fixed-payouts-to-campaign.admin.http";
import { useRemoveFixedPayouttoCampaignAdminHttp } from "../../../../hooks/http/admin/campaigns/remove-fixed-payout-to-campaign.admin.http";
import InputWithCheck from "../../../../components/custom/input-with-check.components";
import {
  type ISocial,
  SocialPlatforms,
} from "../../../../types/models/social.models.types";
import useCampaignStore from "../../../../store/campaign.store";

export const AddFixedPayoutSliderMenuPartial: FunctionComponent = () => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [search, setSearch] = useState("");
  const [paginationData, setPaginationData] = useState<IPagination>();
  const [typing, setTyping] = useState(false);
  const [checkStatus, setCheckStatus] = useState(false);
  const [, setPageNo] = useState(1);

  const { execute: addFixedPayoutHttp, fetching: fetchingAddFixedPayout } =
    useAddFixedPayoutToCampaignAdminHttp();
  const { data: campaign } = useCampaignStore();

  const {
    execute: removeFixedPayoutHttp,
    fetching: fetchingRemoveFixedPayout,
  } = useRemoveFixedPayouttoCampaignAdminHttp();

  const {
    data: existingFixedPayouts,
    setData: setExistingFixedPayouts,
    removeById: removeExistingFixedPayoutById,
  } = useExistingFixedPayoutStore();

  // state for selected fixed payout
  const { data: selectedFixedPayoutData, setData: setSelectedFixedPayoutData } =
    useSelectedFixedPayoutStore();
  // state for removed fixed payout
  const { setData: setRemoveFixedPayoutData, data: removeFixedPayoutData } =
    useRemoveFixedPayoutStore();

  const { campaignId } = useParams();
  const { data: userStoreData, setData } = useUserStore();
  const { toast } = useToast();
  const navigate = useNavigate();

  async function addFixedPayout() {
    if (!campaignId) {
      return;
    }
    if (!selectedFixedPayoutData.length) {
      return;
    }

    const response = await addFixedPayoutHttp({
      body: {
        fixedPayoutSocials: selectedFixedPayoutData.map((i) => ({
          social: i.social.id,
          amount: i.fixedPayout.amount,
        })),
      },
      pathParams: {
        campaignId,
      },
    });

    if (!response.success) {
      return handleHttpError(response, toast);
    }

    toast({
      content: "Fixed payout added successfully",
      toastType: "success",
    });

    setSelectedFixedPayoutData([]);
  }

  async function removeFixedPayout() {
    if (!campaignId) {
      return;
    }
    if (!removeFixedPayoutData.length) {
      return;
    }

    const response = await removeFixedPayoutHttp({
      body: {
        fixedPayoutSocials: removeFixedPayoutData.map((i) => ({
          social: i.social.id,
          amount: i.fixedPayout.amount,
        })),
      },
      pathParams: {
        campaignId,
      },
    });

    if (!response.success) {
      return handleHttpError(response, toast);
    }

    toast({
      content: "Fixed payout removed successfully",
      toastType: "success",
    });
    setRemoveFixedPayoutData([]);
  }

  async function handleUpdate() {
    await addFixedPayout();
    await removeFixedPayout();
    document.getElementById("closeSheet")?.click();
    setTimeout(() => {
      navigate(`/admin/campaigns/${campaignId}/edit`);
    }, 200);
  }

  const {
    execute: getFixedPayoutsHttp,
    fetching: fetchingFixedPayouts,
    cancel: cancelGetFixedPayoutsHttp,
  } = useGetFixedPayoutsAdminHttp();

  const {
    execute: searchSocialHttp,
    fetching: fetchingSocialsSearch,
    cancel: cancelSearchSocialHttp,
  } = useSearchSocialsAdminHttp();

  async function getFixedPayouts(data: IAddedFixedPayout[], pageNo: number) {
    if (!campaignId) return;
    cancelGetFixedPayoutsHttp();
    const response = await getFixedPayoutsHttp({
      body: null,
      pathParams: { campaignId },
      queryParams: {
        page: pageNo,
        status: "active",
      },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }

    // Store all campaign fixed payouts in a variable first
    const campaignFixedPayouts = response.data.campaignFixedPayouts.filter(
      (payout) =>
        !removeFixedPayoutData.some(
          (removed) => removed.social.id === payout.social.id,
        ) &&
        !selectedFixedPayoutData.some(
          (selected) => selected.social.id === payout.social.id,
        ),
    );

    // Set the existing fixed payouts
    setExistingFixedPayouts([...data, ...campaignFixedPayouts]);
    setPaginationData(response.data.pagination);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const debouncedSearch = useCallback(
    debounce(async (searchValue: string) => {
      const searchString = searchValue.trim();
      if (!searchString) {
        return;
      }
      const response = await searchSocialHttp({
        body: null,
        queryParams: {
          searchString: searchString,
          fixedPayout: "true",
          platform: campaign.socialPlatform,
        },
      });
      if (!response.success) {
        return handleHttpError(response, toast);
      }

      setData(response.data.socials);
      setTyping(false);
    }, 500),
    [existingFixedPayouts], // Add existingFixedPayouts as a dependency
  );

  function handleSearch(e: React.ChangeEvent<HTMLInputElement>) {
    cancelSearchSocialHttp();

    setSearch(e.target.value);
    debouncedSearch(e.target.value);
    setTyping(true);
    if (!e.target.value.trim().length) {
      setData([]);
      getFixedPayouts([], 1);
      setTyping(false);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    setData([]);
    getFixedPayouts([], 1);
  }, [checkStatus]);

  const existingFixedPayoutColumns: ColumnDef<IAddedFixedPayout>[] = [
    {
      accessorKey: "id",
      header: "",
      cell: ({ row }) => {
        const { fixedPayout, social } = row.original;
        return (
          <div className="flex items-center">
            {fixedPayout ? (
              <Checkbox
                checkboxType="thunder"
                className="rounded-full w-5 h-5"
                checked={
                  existingFixedPayouts.findIndex(
                    (i) => i.social.id === social.id,
                  ) !== -1
                }
                onClick={() => {
                  removeExistingFixedPayoutById(social.id);
                  setRemoveFixedPayoutData([
                    ...removeFixedPayoutData,
                    row.original,
                  ]);
                }}
              />
            ) : (
              ""
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "social",
      header:
        selectedFixedPayoutData.length > 0 || removeFixedPayoutData.length > 0
          ? ""
          : "User name",
      cell: ({ row }) => {
        const { social } = row.original;
        const { handle, platform, youtube } = social;
        const isYoutube = platform === SocialPlatforms.youtube;
        const channelHandle = youtube?.channelHandle || handle;
        return (
          <div className="flex items-center gap-3 w-44 font-semibold tracking-wider">
            <div className="border-2 rounded-full w-8 h-8 overflow-hidden">
              {isYoutube && youtube?.profileImage ? (
                <img
                  src={youtube.profileImage}
                  alt={channelHandle ?? "YouTube channel"}
                  className="rounded-full w-full h-full object-cover"
                />
              ) : null}
            </div>
            <a
              href={
                isYoutube
                  ? `https://youtube.com/${channelHandle}`
                  : `https://instagram.com/${social.handle}/`
              }
              target="_blank"
              className="max-w-32 text-nowrap underline truncate"
              aria-label={
                isYoutube ? "YouTube profile link" : "Instagram profile link"
              }
              rel="noreferrer"
            >
              {isYoutube ? channelHandle : social.handle}
            </a>
          </div>
        );
      },
    },
    {
      accessorKey: "social",
      header:
        selectedFixedPayoutData.length > 0 || removeFixedPayoutData.length > 0
          ? ""
          : "Followers & engagement",
      cell: ({ row }) => {
        const { social } = row.original;
        const { platform, youtube, engagementRate, followersCount } = social;
        const isYoutube = platform === SocialPlatforms.youtube;
        const youtubeSubs = youtube?.subscriberCount;
        const youtubeEngagement = youtube?.metrics?.engagementRate;
        const countToShow = isYoutube ? youtubeSubs : followersCount;
        const engagementToShow = isYoutube
          ? youtubeEngagement ?? engagementRate
          : engagementRate;

        return (
          <div className="flex justify-between items-center gap-4 w-fit font-semibold lowercase tracking-wider">
            {isYoutube ? <Youtube /> : <InstagramIcon className="w-5 h-5" />}
            <div className="flex justify-start items-center gap-2 w-10">
              {millify(countToShow ?? 0)}
            </div>
            <div className="border-border border-l h-5" />
            <div className="flex justify-start items-center gap-2 w-fit">
              <GrowthIcon />
              {engagementToShow?.toFixed()}%
            </div>
          </div>
        );
      },
    },

    {
      accessorKey: "fixedPayout",
      header:
        selectedFixedPayoutData.length > 0 || removeFixedPayoutData.length > 0
          ? ""
          : "Amount",

      cell: ({ row }) => {
        const { fixedPayout, id } = row.original;

        return (
          <div className="">
            {fixedPayout ? (
              <div className="relative flex items-center font-semibold text-secondary tracking-wider">
                ₹
                <InputWithCheck
                  id={`fixedPayouts.${row.original.social.id}` as string}
                  defaultValue={fixedPayout.amount}
                  onUpdate={(newValue) => {
                    removeExistingFixedPayoutById(row.original.social.id);
                    setSelectedFixedPayoutData([
                      {
                        fixedPayout: {
                          ...fixedPayout,
                          amount: newValue,
                        },
                        social: row.original.social,
                        id: id,
                      },
                      ...selectedFixedPayoutData.filter(
                        (i) => i.social.id !== row.original.social.id,
                      ),
                    ]);
                  }}
                />
              </div>
            ) : (
              <Link to={`/admin/campaigns/${campaignId}/edit/${id}`}>
                <Button className="bg-approvebuttonbg border border-approvebuttonborder font-semibold text-approvebuttontext">
                  Add fixed payout
                </Button>
              </Link>
            )}
          </div>
        );
      },
    },
  ];

  const socialColumns: ColumnDef<ISocial>[] = [
    {
      accessorKey: "id",
      header: "",

      cell: ({ row }) => {
        const { id, fixedPayout } = row.original;
        return (
          <div className="flex items-center">
            {fixedPayout ? (
              <Checkbox
                checkboxType="thunder"
                className="rounded-full w-5 h-5"
                type="button"
                checked={
                  selectedFixedPayoutData.findIndex(
                    (i) => i.social.id === id,
                  ) !== -1 ||
                  existingFixedPayouts.findIndex((i) => i.social.id === id) !==
                    -1
                }
                onClick={() => {
                  const newPayout = {
                    fixedPayout,
                    social: row.original,
                    id,
                  };

                  // Capture current state flags
                  const exists = existingFixedPayouts.some(
                    (i) => i.social.id === id,
                  );
                  const removed = removeFixedPayoutData.some(
                    (i) => i.social.id === id,
                  );
                  const selected = selectedFixedPayoutData.some(
                    (i) => i.social.id === id,
                  );

                  // Remove existing entry from all arrays to prevent duplicates
                  setExistingFixedPayouts(
                    existingFixedPayouts.filter((i) => i.social.id !== id),
                  );
                  setRemoveFixedPayoutData(
                    removeFixedPayoutData.filter((i) => i.social.id !== id),
                  );
                  setSelectedFixedPayoutData(
                    selectedFixedPayoutData.filter((i) => i.social.id !== id),
                  );

                  if (exists) {
                    // If payout exists, move it to removed state
                    setRemoveFixedPayoutData([
                      newPayout,
                      ...removeFixedPayoutData,
                    ]);
                  } else if (removed) {
                    // If payout is removed, restore it to existing state
                    setExistingFixedPayouts([
                      ...existingFixedPayouts,
                      newPayout,
                    ]);
                  } else if (selected) {
                    // If payout is selected, toggle it off
                    setCheckStatus((prev) => !prev);
                    setSearch("");
                  } else {
                    // Else, add it to selected state
                    setSelectedFixedPayoutData([
                      newPayout,
                      ...selectedFixedPayoutData,
                    ]);
                  }
                }}
              />
            ) : (
              ""
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "handle",
      header: "User name",
      cell: ({ row }) => {
        const { handle, platform, youtube } = row.original;
        const isYoutube = platform === SocialPlatforms.youtube;
        const channelHandle = youtube?.channelHandle || handle;
        return (
          <div className="flex items-center gap-3 font-semibold tracking-wider">
            <div className="border-2 rounded-full w-8 h-8 overflow-hidden">
              {isYoutube && youtube?.profileImage ? (
                <img
                  src={youtube.profileImage}
                  alt={channelHandle ?? "YouTube channel"}
                  className="rounded-full w-full h-full object-cover"
                />
              ) : null}
            </div>
            <a
              href={
                isYoutube
                  ? `https://youtube.com/${channelHandle}`
                  : `https://instagram.com/${handle}/`
              }
              target="_blank"
              className="underline"
              aria-label={
                isYoutube ? "YouTube profile link" : "Instagram profile link"
              }
              rel="noreferrer"
            >
              {isYoutube ? channelHandle : handle}
            </a>
          </div>
        );
      },
    },
    {
      accessorKey: "followersCount",
      header: "Followers & engagement",
      cell: ({ row }) => {
        const { platform, youtube, engagementRate, followersCount } =
          row.original;
        const isYoutube = platform === SocialPlatforms.youtube;
        const youtubeSubs = youtube?.subscriberCount;
        const youtubeEngagement = youtube?.metrics?.engagementRate;
        const countToShow = isYoutube ? youtubeSubs : followersCount;
        const engagementToShow = isYoutube
          ? youtubeEngagement ?? engagementRate
          : engagementRate;

        return (
          <div className="flex justify-between items-center gap-4 w-fit font-semibold lowercase tracking-wider">
            {isYoutube ? <Youtube /> : <InstagramIcon className="w-5 h-5" />}
            <div className="flex justify-start items-center gap-2 w-10">
              {millify(countToShow ?? 0)}
            </div>
            <div className="border-border border-l h-5" />
            <div className="flex justify-start items-center gap-2 w-fit">
              <GrowthIcon />
              {engagementToShow?.toFixed()}%
            </div>
          </div>
        );
      },
    },

    {
      accessorKey: "fixedPayout",
      header: "Amount",

      cell: ({ row }) => {
        const { fixedPayout, id } = row.original;

        return (
          <div className="">
            {fixedPayout ? (
              <div className="relative flex items-center font-semibold text-secondary tracking-wider">
                ₹{" "}
                <InputWithCheck
                  id={`fixedPayouts.${row.original.id}` as string}
                  defaultValue={fixedPayout.amount}
                  onUpdate={(newValue) => {
                    const existingIndex = existingFixedPayouts.findIndex(
                      (i) => i.social.id === id,
                    );
                    const removedIndex = removeFixedPayoutData.findIndex(
                      (i) => i.social.id === id,
                    );
                    if (existingIndex !== -1) {
                      const updatedPayouts = [...existingFixedPayouts];
                      updatedPayouts[existingIndex].fixedPayout.amount =
                        newValue;
                      setExistingFixedPayouts(updatedPayouts);
                      setSelectedFixedPayoutData([
                        {
                          fixedPayout: {
                            ...fixedPayout,
                            amount: newValue,
                          },
                          social: row.original,
                          id: id,
                        },
                        ...selectedFixedPayoutData.filter(
                          (i) => i.social.id !== id,
                        ),
                      ]);
                      removeExistingFixedPayoutById(id);
                    }
                    if (removedIndex !== -1) {
                      const updatedPayouts = [...removeFixedPayoutData];
                      updatedPayouts[removedIndex].fixedPayout.amount =
                        newValue;
                      setRemoveFixedPayoutData(updatedPayouts);
                      setSelectedFixedPayoutData([
                        {
                          fixedPayout: {
                            ...fixedPayout,
                            amount: newValue,
                          },
                          social: row.original,
                          id: id,
                        },
                        ...selectedFixedPayoutData.filter(
                          (i) => i.social.id !== id,
                        ),
                      ]);
                      setRemoveFixedPayoutData(
                        removeFixedPayoutData.filter((i) => i.social.id !== id),
                      );
                    } else {
                      const selectedIndex = selectedFixedPayoutData.findIndex(
                        (i) => i.social.id === id,
                      );
                      if (selectedIndex !== -1) {
                        const updatedSelectedPayouts = [
                          ...selectedFixedPayoutData,
                        ];
                        updatedSelectedPayouts[
                          selectedIndex
                        ].fixedPayout.amount = newValue;
                        setSelectedFixedPayoutData(updatedSelectedPayouts);
                      }
                    }
                    setData([
                      {
                        ...row.original,
                        fixedPayout: {
                          ...fixedPayout,
                          amount: newValue,
                        },
                      },
                      ...userStoreData.filter((i) => i.id !== id),
                    ]);
                  }}
                />
              </div>
            ) : (
              <Link to={`/admin/campaigns/${campaignId}/edit/${id}`}>
                <Button className="bg-approvebuttonbg border border-approvebuttonborder font-semibold text-approvebuttontext">
                  Add fixed payout
                </Button>
              </Link>
            )}
          </div>
        );
      },
    },
  ];

  const selectedFixedPayoutColums: ColumnDef<IAddedFixedPayout>[] = [
    {
      accessorKey: "id",
      header: "",
      cell: ({ row }) => {
        const { social } = row.original;
        return (
          <div className="flex items-center">
            <Checkbox
              checkboxType="thunder"
              className="rounded-full w-5 h-5"
              checked={
                selectedFixedPayoutData.findIndex(
                  (i) => i.social.id === social.id,
                ) !== -1
              }
              onClick={() => {
                setSelectedFixedPayoutData(
                  selectedFixedPayoutData.filter(
                    (i) => i.social.id !== social.id,
                  ),
                );
                setCheckStatus(!checkStatus);
              }}
            />
          </div>
        );
      },
    },
    {
      accessorKey: "social",
      header: "User name",
      cell: ({ row }) => {
        const { social } = row.original;
        const { handle, platform, youtube } = social;
        const isYoutube = platform === SocialPlatforms.youtube;
        const channelHandle = youtube?.channelHandle || handle;
        return (
          <div className="flex items-center gap-3 w-44 font-semibold tracking-wider">
            <div className="border-2 rounded-full w-8 h-8 overflow-hidden">
              {isYoutube && youtube?.profileImage ? (
                <img
                  src={youtube.profileImage}
                  alt={channelHandle ?? "YouTube channel"}
                  className="rounded-full w-full h-full object-cover"
                />
              ) : null}
            </div>
            <a
              href={
                isYoutube
                  ? `https://youtube.com/${channelHandle}`
                  : `https://instagram.com/${social.handle}/`
              }
              target="_blank"
              className="max-w-32 text-nowrap underline truncate"
              aria-label={
                isYoutube ? "YouTube profile link" : "Instagram profile link"
              }
              rel="noreferrer"
            >
              {isYoutube ? channelHandle : social.handle}
            </a>
          </div>
        );
      },
    },
    {
      accessorKey: "social",
      header: "Followers & engagement",
      cell: ({ row }) => {
        const { social } = row.original;
        const { platform, youtube, engagementRate, followersCount } = social;
        const isYoutube = platform === SocialPlatforms.youtube;
        const youtubeSubs = youtube?.subscriberCount;
        const youtubeEngagement = youtube?.metrics?.engagementRate;
        const countToShow = isYoutube ? youtubeSubs : followersCount;
        const engagementToShow = isYoutube
          ? youtubeEngagement ?? engagementRate
          : engagementRate;

        return (
          <div className="flex justify-between items-center gap-4 w-fit font-semibold lowercase tracking-wider">
            {isYoutube ? <Youtube /> : <InstagramIcon className="w-5 h-5" />}
            <div className="flex justify-start items-center gap-2 w-10">
              {millify(countToShow ?? 0)}
            </div>
            <div className="border-border border-l h-5" />
            <div className="flex justify-start items-center gap-2 w-fit">
              <GrowthIcon />
              {engagementToShow?.toFixed()}%
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "fixedPayout",
      header: "Amount",

      cell: ({ row }) => {
        const { fixedPayout, social } = row.original;

        return (
          <div className="">
            <div className="relative flex items-center font-semibold text-secondary tracking-wider">
              ₹{" "}
              <InputWithCheck
                id={`fixedPayouts.${social.id}` as string}
                defaultValue={fixedPayout.amount}
                onUpdate={(newValue) => {
                  setSelectedFixedPayoutData(
                    selectedFixedPayoutData.map((item) => {
                      if (item.social.id === social.id) {
                        return {
                          fixedPayout: {
                            ...item.fixedPayout,
                            amount: newValue,
                          },
                          social: social,
                          id: social.id,
                        };
                      }
                      return item;
                    }),
                  );
                }}
              />
            </div>
          </div>
        );
      },
    },
  ];

  const removedFixedPayoutColumns: ColumnDef<IAddedFixedPayout>[] = [
    {
      accessorKey: "id",
      header: "",
      cell: ({ row }) => {
        const { social } = row.original;
        return (
          <div className="flex items-center">
            <Checkbox
              checkboxType="thunder"
              className="rounded-full w-5 h-5"
              checked={false}
              onClick={() => {
                setRemoveFixedPayoutData(
                  removeFixedPayoutData.filter(
                    (i) => i.social.id !== social.id,
                  ),
                );

                setExistingFixedPayouts([
                  ...existingFixedPayouts,
                  row.original,
                ]);
              }}
            />
          </div>
        );
      },
    },
    {
      accessorKey: "social",
      header: selectedFixedPayoutData.length > 0 ? "" : "User name",
      cell: ({ row }) => {
        const { social } = row.original;
        const { handle, platform, youtube } = social;
        const isYoutube = platform === SocialPlatforms.youtube;
        const channelHandle = youtube?.channelHandle || handle;
        return (
          <div className="flex items-center gap-3 w-44 font-semibold tracking-wider">
            <div className="border-2 rounded-full w-8 h-8 overflow-hidden">
              {isYoutube && youtube?.profileImage ? (
                <img
                  src={youtube.profileImage}
                  alt={channelHandle ?? "YouTube channel"}
                  className="rounded-full w-full h-full object-cover"
                />
              ) : null}
            </div>
            <a
              href={
                isYoutube
                  ? `https://youtube.com/${channelHandle}`
                  : `https://instagram.com/${social.handle}/`
              }
              target="_blank"
              className="max-w-32 text-nowrap underline truncate"
              aria-label={
                isYoutube ? "YouTube profile link" : "Instagram profile link"
              }
              rel="noreferrer"
            >
              {isYoutube ? channelHandle : social.handle}
            </a>
          </div>
        );
      },
    },
    {
      accessorKey: "social",
      header:
        selectedFixedPayoutData.length > 0 ? "" : "Followers & engagement",
      cell: ({ row }) => {
        const { social } = row.original;
        const { platform, youtube, engagementRate, followersCount } = social;
        const isYoutube = platform === SocialPlatforms.youtube;
        const youtubeSubs = youtube?.subscriberCount;
        const youtubeEngagement = youtube?.metrics?.engagementRate;
        const countToShow = isYoutube ? youtubeSubs : followersCount;
        const engagementToShow = isYoutube
          ? youtubeEngagement ?? engagementRate
          : engagementRate;

        return (
          <div className="flex justify-between items-center gap-4 w-fit font-semibold lowercase tracking-wider">
            {isYoutube ? <Youtube /> : <InstagramIcon className="w-5 h-5" />}
            <div className="flex justify-start items-center gap-2 w-10">
              {millify(countToShow ?? 0)}
            </div>
            <div className="border-border border-l h-5" />
            <div className="flex justify-start items-center gap-2 w-fit">
              <GrowthIcon />
              {engagementToShow?.toFixed()}%
            </div>
          </div>
        );
      },
    },
  ];

  const socialTable = useReactTable({
    data: userStoreData,
    columns: socialColumns,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    manualPagination: true,
  });

  const existingFixedPayoutTable = useReactTable({
    data: existingFixedPayouts,
    columns: existingFixedPayoutColumns,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    manualPagination: true,
  });

  const selectedFixedPayoutTable = useReactTable({
    data: selectedFixedPayoutData,
    columns: selectedFixedPayoutColums,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    manualPagination: true,
  });

  const removedFixedPayoutTable = useReactTable({
    data: removeFixedPayoutData,
    columns: removedFixedPayoutColumns,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    manualPagination: true,
  });

  return (
    <div className="relative flex flex-col gap-5 w-full h-full">
      <div className="flex justify-between items-center w-full">
        <p className="text-2xl">Add fixed payout</p>
        {/* <Link className="mr-16" to={`${window.location.pathname}/import`}>
					<Button className="flex items-center gap-3 bg-tertiarybuttonbg px-3 py-7 border border-border rounded-xl font-semibold">
						<DocumentSmall />
						Upload CSV
					</Button>
				</Link> */}
      </div>
      <div className="relative flex items-center bg-bginput py-2 pl-5 border border-border rounded-10">
        <SearchIcon />
        <Input
          value={search}
          onChange={(e) => {
            handleSearch(e);
          }}
          className="bg-transparent border-none w-full font-semibold text-base placeholder:text-secondary"
          placeholder="Search by name, instagram ID, and number"
        />
        {search.trim().length > 0 && (
          <XIcon
            onClick={() => {
              setSearch("");
              setData([]);
              getFixedPayouts([], 1);
            }}
            className="right-5 absolute cursor-pointer"
          />
        )}
      </div>
      {(existingFixedPayouts.length === 0 && fetchingFixedPayouts) ||
      fetchingSocialsSearch ||
      typing ? (
        <div className="flex justify-center items-center pt-20 w-full h-full">
          <Spinner />
        </div>
      ) : (
        <ScrollArea className="flex flex-col w-full h-full">
          {search.trim().length > 0 ? (
            <DataTableComponents
              table={socialTable as ReturnType<typeof useReactTable>}
              columns={socialColumns as ColumnDef<ISocial>[]}
              setPageNo={() => {}}
              loading={fetchingSocialsSearch}
              tableType="fixedPayout"
            />
          ) : (
            <div className="flex flex-col">
              {selectedFixedPayoutData.length > 0 && (
                <DataTableComponents
                  table={
                    selectedFixedPayoutTable as ReturnType<typeof useReactTable>
                  }
                  columns={
                    selectedFixedPayoutColums as ColumnDef<IAddedFixedPayout>[]
                  }
                  setPageNo={() => {}}
                  loading={false}
                  tableType="fixedPayout"
                  tableBodyClass="bg-[#56fe2c16]"
                />
              )}
              {removeFixedPayoutData.length > 0 && (
                <DataTableComponents
                  table={
                    removedFixedPayoutTable as ReturnType<typeof useReactTable>
                  }
                  columns={
                    removedFixedPayoutColumns as ColumnDef<IAddedFixedPayout>[]
                  }
                  setPageNo={() => {}}
                  loading={false}
                  tableType="fixedPayout"
                  tableBodyClass="bg-[#ff000016]"
                />
              )}
              {existingFixedPayouts.length > 0 ? (
                <DataTableComponents
                  table={
                    existingFixedPayoutTable as ReturnType<typeof useReactTable>
                  }
                  columns={
                    existingFixedPayoutColumns as ColumnDef<IAddedFixedPayout>[]
                  }
                  setPageNo={() => {
                    setPageNo((prev) => {
                      const nextPage = prev + 1;
                      getFixedPayouts(existingFixedPayouts, nextPage);
                      return nextPage;
                    });
                  }}
                  loading={fetchingFixedPayouts}
                  tableType="fixedPayout"
                  paginationData={paginationData}
                />
              ) : (
                <div className="w-full">
                  {selectedFixedPayoutData.length === 0 &&
                    removeFixedPayoutData.length === 0 && (
                      <div className="flex justify-center items-center w-full h-96">
                        <p className="text-center text-secondary">
                          No fixed payouts found
                        </p>
                      </div>
                    )}
                </div>
              )}
            </div>
          )}
        </ScrollArea>
      )}
      <div className="w-full">
        <Button
          onClick={() => {
            handleUpdate();
          }}
          disabled={
            (!selectedFixedPayoutData.length &&
              !removeFixedPayoutData.length) ||
            fetchingAddFixedPayout ||
            fetchingRemoveFixedPayout
          }
          className="bg-primarybuttonbg w-full h-14 font-semibold text-primarybuttontext"
        >
          {fetchingAddFixedPayout || fetchingRemoveFixedPayout ? (
            <Spinner width={20} />
          ) : (
            "Update"
          )}
        </Button>
      </div>
    </div>
  );
};
